<script setup lang="ts">
import type { Popover } from 'primevue';
import DOMPurify from 'dompurify';
import { computed, ref } from 'vue';

import type { SettingsNotificationsNamespace } from '@modules/general-settings/submodules/notifications/types';
import type { IHeaderNotificationPayload } from '@modules/notification/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import { ERouteNamespace } from '@modules/core/services/router/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS } from '@modules/general-settings/submodules/notifications/constants';
import { ESettingsNotificationsNamespace } from '@modules/general-settings/submodules/notifications/types';
import { useLocale } from '@modules/i18n/composables';
import useHeaderNotificationMarkAsReadMutation
  from '@modules/notification/mutations/useHeaderNotificationMarkAsReadMutation';
import useHeaderNotificationQuery from '@modules/notification/queries/useHeaderNotificationQuery';
import { useUserSupervision } from '@modules/user/composables';

export interface IHeaderNotificationProps {
  namespace: ERouteNamespace.AFFILIATE | ERouteNamespace.ANONYMOUS;
}

const props = defineProps<IHeaderNotificationProps>();
const panel = ref<InstanceType<typeof Popover>>();

const { currentLocale } = useLocale();
const { isSupervision } = useUserSupervision();

const payload = computed<IHeaderNotificationPayload | undefined>(() => {
  let namespace: SettingsNotificationsNamespace;

  switch (props.namespace) {
    case ERouteNamespace.AFFILIATE:
      namespace = ESettingsNotificationsNamespace.AFFILIATE;
      break;
    case ERouteNamespace.ANONYMOUS:
      namespace = ESettingsNotificationsNamespace.ANONYMOUS;
      break;
    default:
      return undefined;
  }

  return { namespace, locale: currentLocale.value };
});

const { data, isFetching } = useHeaderNotificationQuery(payload);
const { mutate, isLoading } = useHeaderNotificationMarkAsReadMutation();

const isHidden = computed<boolean>(() => (data.value && !isFetching.value ? data.value.hide_notification : true));

function markAsRead(): void {
  if (data.value?.notification && !isLoading.value && !isSupervision.value) {
    mutate(data.value.notification.id);
  }
}

function toggleMenu(event: Event): void {
  if (!isHidden.value)
    markAsRead();
  panel.value?.toggle(event);
}
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <div v-data-qa-auto-id="'HeaderNotification'">
    <!-- affiliate notification -->
    <template v-if="namespace === ERouteNamespace.AFFILIATE">
      <div class="relative">
        <VIconButton
          icon="pi-bell"
          text-color="white"
          indicator
          @click="toggleMenu"
        />
        <span
          v-if="!isHidden"
          class="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-green-200"
        />
      </div>
      <POverlayPanel
        v-if="data"
        ref="panel"
        :pt="{
          root: {
            class: 'before:content-[none] after:content-[none] min-w-56 max-w-2xl',
            style: { 'background-color': data.notification?.header_color },
          },
          content: { class: '!p-0' },
        }"
      >
        <div
          v-if="data.notification"
          class="max-h-96 overflow-y-auto p-6"
          :style="{
            color: data.notification.text_color,
          }"
          v-html="DOMPurify.sanitize(data.notification.body)"
        />
        <div
          v-else
          class="p-6"
        >
          {{ $getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'NO_ANY_NOTIFICATION') }}
        </div>
      </POverlayPanel>
    </template>
    <!-- anonymous notification -->
    <template
      v-else
    >
      <Transition
        name="slide"
      >
        <div
          v-if="data && !data.hide_notification"
          class="overflow-hidden"
        >
          <div
            class="max-h-[200px] overflow-y-auto p-6"
            :style="{
              'background-color': data.notification.header_color,
              'color': data.notification.text_color,
            }"
            v-html="DOMPurify.sanitize(data.notification?.body)"
          />
        </div>
      </Transition>
    </template>
  </div>
</template>

<style scoped lang="scss">
.slide-enter-active,
.slide-leave-active {
  max-height: 150px;
  opacity: 1;
  transition: all 600ms ease-in;
}

.slide-enter-from {
  max-height: 0;
  opacity: 0;
}

.slide-leave-to {
  max-height: 0;
}
</style>
